import React, { useState, useEffect } from "react";
import formatNumber from "../../services/formatNumber";
import AppTooltip from "../AppTooltip";
import Loader from "../Loader/Loader";
import HeartIcon from "../../assets/icons/heart";
import ApiNoContent from "../ApiKeys/ApiNoContent";

type ClientsDataType = {
  client_id: number;
  display_name: string;
  total_search: string;
  total_share: string;
  total_trending: string;
  total_view: string;
};

type AppAnalyticsProps = {
  clientsData: ClientsDataType[] | null;
  isLoading?: boolean;
};

const AppAnalytics: React.FC<AppAnalyticsProps> = ({ clientsData, isLoading = false }) => {
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  
  const [showTimeout, setShowTimeout] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent, text: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 2,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (!isLoading && !clientsData) {
      timer = setTimeout(() => {
        setShowTimeout(true);
      }, 5000);
    } else {
      setShowTimeout(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isLoading, clientsData]);

  return (
    <div className="table-container">
      <h2>App Analytics</h2>
      {isLoading ? (
        <Loader />
      ) : !clientsData && !showTimeout ? (
        <Loader />
      ) : !clientsData || clientsData.length === 0 ? (
          <ApiNoContent
            icon={<HeartIcon />}
            iconClassName="api-keys__heart-icon"
            title="Analytics Not Found Yet"
            description=""
            buttonText="Get Started"
            buttonType="redirect"
            onButtonClick={() => window.location.href = "/api-keys"}
          />
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Application Name</th>
                <th>Trending</th>
                <th>Search</th>
                <th>View</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {clientsData.map((app, index) => (
                <tr key={index}>
                  <td>{app.display_name}</td>
                  <td>
                    <span
                      onMouseEnter={(e) =>
                        handleMouseEnter(
                          e,
                          `${Number(app.total_trending).toLocaleString()}`
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {formatNumber(Number(app.total_trending))}
                    </span>
                  </td>
                  <td>
                    <span
                      onMouseEnter={(e) =>
                        handleMouseEnter(
                          e,
                          `${Number(app.total_search).toLocaleString()}`
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {formatNumber(Number(app.total_search))}
                    </span>
                  </td>
                  <td>
                    <span
                      onMouseEnter={(e) =>
                        handleMouseEnter(
                          e,
                          `${Number(app.total_view).toLocaleString()}`
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {formatNumber(Number(app.total_view))}
                    </span>
                  </td>
                  <td>
                    <span
                      onMouseEnter={(e) =>
                        handleMouseEnter(
                          e,
                          `${Number(app.total_share).toLocaleString()}`
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {formatNumber(Number(app.total_share))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {tooltip && (
        <AppTooltip
          text={tooltip.text}
          position={{ x: tooltip.x, y: tooltip.y }}
        />
      )}
    </div>
  );
};

export default AppAnalytics;
